import { ClodoApi, ensureLoaded, setFolderContent } from '@clodo/api';

// Façade web au-dessus de @clodo/api.
//
// Le protocole, l'arbre et les URLs signées vivent désormais dans le paquet
// partagé avec l'app mobile ; ce fichier ne garde que ce qui est propre au web :
// l'API à callbacks attendue par les composants, les alertes navigateur et le
// téléchargement par ancre. Sa surface publique est inchangée, aucun des
// composants consommant BrowserContext n'a eu à bouger.

// localStorage est synchrone ; ClodoApi accepte un stockage asynchrone, les deux
// se rejoignent via await. Côté mobile ce sera AsyncStorage.
const webStorage = {
    get : key => localStorage.getItem(key),
    set : (key, value) => localStorage.setItem(key, value),
    remove : key => localStorage.removeItem(key)
};

class ItemsManipulator {
    constructor() {
        this.elements = {};
        this.path = [];
        this.name = "";
        this.handleCurrentPath = null;
        this.handleUser = null;
        this.onExpired = null;

        this.api = new ClodoApi({
            apiUrl : process.env.REACT_APP_API_URL,
            storage : webStorage
        });
        // localStorage étant synchrone, la restauration est immédiate : pas
        // besoin d'attendre restore() comme le fera le mobile.
        this.api.sessionId = localStorage.getItem('session_id');
        this.api.userLogin = localStorage.getItem('user_login');
        this.api.onExpired = () => { if(this.onExpired){ this.onExpired(); } };
    }

    // Lus par Uploader et par les composants ; conservés en lecture seule.
    get session_id(){ return this.api.sessionId; }
    get userLogin(){ return this.api.userLogin; }

    // Restitue le comportement historique : une alerte sur les erreurs de
    // transport, un log sur les refus métier. Les composants existants ne
    // traitent pas les erreurs eux-mêmes.
    _report(error, onError){
        if(error.kind === 'network'){
            alert('connection error');
            return;
        }
        if(error.kind === 'http'){
            alert('server error :' + error.message);
            return;
        }
        if(error.kind !== 'expired'){
            console.log(error.data || error);
        }
        if(onError){ onError(error.data); }
    }

    login(username, password, onResult){
        const path = this.path;
        this.api.login(username, password)
            .then(data => {
                // Charge le contenu racine (arbre latéral) et prépare l'affichage
                // du dossier courant. Sur /login, handleCurrentPath ne navigue
                // pas : la redirection finale est pilotée par LoginPage.
                this.handleContent(data.content, []);
                this.handleUser(data.user);
                this.handleCurrentPath(path);
                if(onResult){ onResult(true); }
            })
            .catch(error => {
                this._report(error);
                if(onResult){ onResult(false); }
            });
    }

    logout() {
        this.elements.child = [];
        this.api.logout().then(() => this.handleCurrentPath([]));
    }

    // URL de vignette/lecture, construite depuis les champs dl_exp/dl_sig que
    // seule l'action `get` attache aux fichiers.
    signedDownloadUrl(pathArray, name, exp, sig) {
        return this.api.downloadUrl(pathArray, name, exp, sig);
    }

    jellyfinImageUrl(id, exp, sig) {
        return this.api.jellyfinImageUrl(id, exp, sig);
    }

    // Téléchargement explicite : on demande d'abord un lien signé frais (la
    // session reste dans le corps du POST, jamais dans l'URL), puis on déclenche
    // l'ancre. C'est le seul endroit franchement navigateur du fichier — côté
    // mobile ce sera expo-file-system.
    download(path) {
        const filename = path[path.length - 1];
        this.api.signUrl(path)
            .then(url => {
                const a = document.createElement("a");
                a.href = url;
                a.target = "_blank";
                a.rel = "noopener noreferrer";
                a.setAttribute("download", filename);
                a.click();
            })
            .catch(error => this._report(error));
    }

    create(path){
        this.api.create(path)
            .then(data => {
                path.pop();
                this.handleContent(data.content, path);
                this.handleCurrentPath(path);
            })
            .catch(error => this._report(error));
    }

    rename(path, newName){
        this.api.rename(path, newName)
            .then(data => {
                path.pop();
                this.handleContent(data.content, path);
                this.handleCurrentPath(path);
            })
            .catch(error => this._report(error));
    }

    paste(path, newPath, action){
        this.api.paste(path, newPath, action)
            .then(data => {
                this.handleContent(data.content, newPath);
                this.handleCurrentPath(newPath);
                if(action === 'cut'){
                    // Le dossier d'origine a perdu un élément : le recharger.
                    path.pop();
                    this.get(path);
                }
            })
            .catch(error => this._report(error));
    }

    delete(path){
        this.api.remove(path)
            .then(data => {
                path.pop();
                this.handleContent(data.content, path);
                this.handleCurrentPath(path);
            })
            .catch(error => this._report(error));
    }

    // Charge les niveaux manquants jusqu'à `path` puis déclenche le rendu.
    get(path){
        const target = Array.from(path);
        ensureLoaded(this.api, this.elements, target)
            .then(reached => {
                // Repli sur un préfixe valide (deep-link périmé) : on rend
                // toujours. Cible pleinement atteinte : on ne rend que si
                // l'utilisateur n'a pas navigué ailleurs entre-temps, sinon une
                // réponse tardive le ramènerait en arrière.
                if(reached.length < target.length || target.toString() === this.path.toString()){
                    this.handleCurrentPath(reached);
                }
            })
            .catch(error => this._report(error));
    }

    search(query, limit, callback){
        this.api.search(query, limit)
            .then(content => callback(content))
            .catch(error => this._report(error));
    }

    init(){
        const path = Array.from(this.path);
        this.api.get([])
            .then(data => {
                this.handleContent(data.content, []);
                this.handleCurrentPath(path);
                this.getUser();
            })
            .catch(error => this._report(error));
    }

    getUser(){
        this.api.getUser()
            .then(user => this.handleUser(user))
            .catch(error => this._report(error));
    }

    createShare(path, password, expiresAt, callback) {
        this.api.createShare(path, password, expiresAt)
            .then(token => callback(token))
            .catch(error => this._report(error));
    }

    updateShare(token, expiresAt, callback) {
        this.api.updateShare(token, expiresAt)
            .then(() => callback && callback())
            .catch(error => this._report(error));
    }

    deleteShare(token, callback) {
        this.api.deleteShare(token)
            .then(() => callback && callback())
            .catch(error => this._report(error));
    }

    listUsers(callback){
        this.api.listUsers()
            .then(users => callback(users))
            .catch(error => this._report(error));
    }

    createUser(user, callback){
        this.api.createUser(user)
            .then(data => callback && callback(data))
            .catch(error => this._report(error, callback));
    }

    updateUser(user, callback){
        this.api.updateUser(user)
            .then(data => callback && callback(data))
            .catch(error => this._report(error, callback));
    }

    deleteUser(login, callback){
        this.api.deleteUser(login)
            .then(data => callback && callback(data))
            .catch(error => this._report(error, callback));
    }

    handleContent(content, path){
        setFolderContent(this.elements, path, content);
    }
}

export default ItemsManipulator
